/**
 * Export / import CSV + sauvegarde JSON des données utilisateur.
 *
 * Parsing/sérialisation délégués au module pur `domain/csv.ts` ; ce service
 * orchestre PocketBase. Import idempotent (dédup sur clés naturelles) : les
 * aliments manquants référencés par une consommation sont créés à la volée
 * depuis les colonnes de composition embarquées.
 */

import {
  type BackupData,
  parseBackup,
  parseCsv,
  SPORT_ORDER,
  serializeBackup,
  serializeCsv,
  toDate,
} from '@/domain';
import { currentUserId, pb } from '@/lib/pocketbase';
import type {
  ConsumptionRecord,
  ExerciseEntryRecord,
  FoodFavoriteRecord,
  FoodRecord,
  MilestoneRecord,
  ProfileRecord,
  WaterEntryRecord,
  WeightEntryRecord,
} from '@/types/records';

/** Rapport d'import affiché à l'utilisateur. */
export interface ImportReport {
  entity: string;
  created: number;
  updated: number;
  skipped: number;
  errors: string[];
}

const FOOD_COLUMNS = [
  'kcal_100g',
  'protein_100g',
  'carbs_100g',
  'sugars_100g',
  'fat_100g',
  'saturated_100g',
  'fiber_100g',
  'salt_100g',
] as const;

// --- Export -----------------------------------------------------------------

export async function exportWeightsCsv(): Promise<string> {
  const recs = await pb
    .collection('weight_entries')
    .getFullList<WeightEntryRecord>({ sort: 'date' });
  return serializeCsv(
    recs.map((r) => ({ date: r.date.slice(0, 10), weight_kg: r.weight_kg })),
    ['date', 'weight_kg'],
  );
}

export async function exportFoodsCsv(): Promise<string> {
  // Aliments personnels uniquement (le catalogue public est seedé côté serveur).
  const uid = currentUserId();
  const recs = await pb
    .collection('foods')
    .getFullList<FoodRecord>({ filter: `user = ${JSON.stringify(uid)}`, sort: 'name' });
  return serializeCsv(recs as unknown as Record<string, unknown>[], ['name', ...FOOD_COLUMNS]);
}

export async function exportConsumptionsCsv(): Promise<string> {
  const recs = await pb
    .collection('consumption_entries')
    .getFullList<ConsumptionRecord>({ sort: 'datetime', expand: 'food' });
  const rows = recs.map((r) => {
    const f = r.expand?.food;
    return {
      datetime: r.datetime,
      meal: r.meal ?? '',
      grams: r.grams,
      food_name: f?.name ?? '',
      ...Object.fromEntries(FOOD_COLUMNS.map((c) => [c, f?.[c] ?? 0])),
    };
  });
  return serializeCsv(rows, ['datetime', 'meal', 'grams', 'food_name', ...FOOD_COLUMNS]);
}

export async function exportWaterCsv(): Promise<string> {
  const recs = await pb
    .collection('water_entries')
    .getFullList<WaterEntryRecord>({ sort: 'datetime' });
  return serializeCsv(
    recs.map((r) => ({ datetime: r.datetime, ml: r.ml })),
    ['datetime', 'ml'],
  );
}

const EXERCISE_COLUMNS = ['datetime', 'activity', 'kcal_burned', 'duration_min', 'note'] as const;

export async function exportExerciseCsv(): Promise<string> {
  const recs = await pb
    .collection('exercise_entries')
    .getFullList<ExerciseEntryRecord>({ sort: 'datetime' });
  return serializeCsv(
    recs.map((r) => ({
      datetime: r.datetime,
      activity: r.activity ?? '',
      kcal_burned: r.kcal_burned,
      duration_min: r.duration_min,
      note: r.note ?? '',
    })),
    [...EXERCISE_COLUMNS],
  );
}

// --- Import -----------------------------------------------------------------

/** Importe des prises d'eau : déduplication sur (datetime, ml). */
export async function importWaterCsv(text: string): Promise<ImportReport> {
  const report: ImportReport = {
    entity: 'Hydratation',
    created: 0,
    updated: 0,
    skipped: 0,
    errors: [],
  };
  const { rows } = parseCsv(text);
  const seen = await existingWaterKeys();

  for (const [i, row] of rows.entries()) {
    const datetime = row.datetime?.trim();
    const ml = Number(row.ml);
    if (!datetime || !Number.isFinite(ml)) {
      report.errors.push(`Ligne ${i + 2} : datetime ou ml invalide`);
      continue;
    }
    const key = `${normalizeInstant(datetime)}|${ml}`;
    if (seen.has(key)) {
      report.skipped++;
      continue;
    }
    try {
      await pb.collection('water_entries').create({ user: currentUserId(), datetime, ml });
      seen.add(key);
      report.created++;
    } catch (e) {
      report.errors.push(`Ligne ${i + 2} : ${(e as Error).message}`);
    }
  }
  return report;
}

/** Importe des séances de sport : déduplication sur (datetime, activité, kcal, durée). */
export async function importExerciseCsv(text: string): Promise<ImportReport> {
  const report: ImportReport = { entity: 'Sport', created: 0, updated: 0, skipped: 0, errors: [] };
  const uid = currentUserId();
  const { rows } = parseCsv(text);
  const seen = await existingExerciseKeys();

  for (const [i, row] of rows.entries()) {
    const created = await createExerciseFromRow(row, uid, seen, report);
    if (created === 'invalid') report.errors.push(`Ligne ${i + 2} : datetime ou kcal invalide`);
  }
  return report;
}

/** Importe des pesées : déduplication sur la date (une pesée par jour). */
export async function importWeightsCsv(text: string): Promise<ImportReport> {
  const report: ImportReport = { entity: 'Pesées', created: 0, updated: 0, skipped: 0, errors: [] };
  const uid = currentUserId();
  const { rows } = parseCsv(text);

  const existing = await pb.collection('weight_entries').getFullList<WeightEntryRecord>();
  const byDate = new Map(existing.map((r) => [r.date.slice(0, 10), r]));

  for (const [i, row] of rows.entries()) {
    const date = row.date?.trim();
    const kg = Number(row.weight_kg);
    if (!date || !Number.isFinite(kg)) {
      report.errors.push(`Ligne ${i + 2} : date ou poids invalide`);
      continue;
    }
    if (byDate.has(date)) {
      report.skipped++;
      continue;
    }
    try {
      await pb.collection('weight_entries').create({ user: uid, date, weight_kg: kg });
      report.created++;
    } catch (e) {
      report.errors.push(`Ligne ${i + 2} : ${(e as Error).message}`);
    }
  }
  return report;
}

/** Importe des aliments personnels : déduplication sur le nom (insensible casse). */
export async function importFoodsCsv(text: string): Promise<ImportReport> {
  const report: ImportReport = {
    entity: 'Aliments',
    created: 0,
    updated: 0,
    skipped: 0,
    errors: [],
  };
  const { rows } = parseCsv(text);
  const index = await buildFoodIndex();

  for (const [i, row] of rows.entries()) {
    const name = row.name?.trim();
    if (!name) {
      report.errors.push(`Ligne ${i + 2} : nom manquant`);
      continue;
    }
    if (index.has(foodKey(name))) {
      report.skipped++;
      continue;
    }
    try {
      const rec = await createFood(name, row);
      index.set(foodKey(name), rec.id);
      report.created++;
    } catch (e) {
      report.errors.push(`Ligne ${i + 2} : ${(e as Error).message}`);
    }
  }
  return report;
}

/**
 * Importe des consommations : aliments inconnus créés à la volée depuis les
 * colonnes de composition. Dédup sur (datetime, aliment, grammes).
 */
export async function importConsumptionsCsv(text: string): Promise<ImportReport> {
  const report: ImportReport = {
    entity: 'Consommations',
    created: 0,
    updated: 0,
    skipped: 0,
    errors: [],
  };
  const uid = currentUserId();
  const { rows } = parseCsv(text);
  const foodIndex = await buildFoodIndex();

  const existing = await pb.collection('consumption_entries').getFullList<ConsumptionRecord>();
  const seen = new Set(existing.map((r) => `${normalizeInstant(r.datetime)}|${r.food}|${r.grams}`));

  for (const [i, row] of rows.entries()) {
    const name = row.food_name?.trim();
    const grams = Number(row.grams);
    const datetime = row.datetime?.trim();
    if (!name || !datetime || !Number.isFinite(grams)) {
      report.errors.push(`Ligne ${i + 2} : champ requis invalide`);
      continue;
    }
    try {
      let foodId = foodIndex.get(foodKey(name));
      if (!foodId) {
        const rec = await createFood(name, row);
        foodId = rec.id;
        foodIndex.set(foodKey(name), foodId);
      }
      const key = `${normalizeInstant(datetime)}|${foodId}|${grams}`;
      if (seen.has(key)) {
        report.skipped++;
        continue;
      }
      await pb.collection('consumption_entries').create({
        user: uid,
        food: foodId,
        grams,
        datetime,
        meal: row.meal || undefined,
      });
      seen.add(key);
      report.created++;
    } catch (e) {
      report.errors.push(`Ligne ${i + 2} : ${(e as Error).message}`);
    }
  }
  return report;
}

// --- Helpers ----------------------------------------------------------------

// Instant ISO canonique comparable quelle que soit la forme (« T »/espace, ms ou
// non) : sans ça la clé de dédup CSV vs PocketBase diffère → doublons. Repli sur
// la chaîne brute si non parsable.
function normalizeInstant(value: string): string {
  const d = toDate(value);
  return Number.isNaN(d.getTime()) ? value : d.toISOString();
}

/** Clé de nom d'aliment normalisée (trim + minuscule) pour une dédup cohérente. */
function foodKey(name: string): string {
  return name.trim().toLowerCase();
}

/**
 * Parse les colonnes de composition. Vide → 0 (champ facultatif) ; non numérique
 * signalé (ne pas corrompre silencieusement). Accepte la virgule décimale FR.
 */
function parseComposition(row: Record<string, unknown>): {
  values: Record<string, number>;
  invalid: string[];
} {
  const values: Record<string, number> = {};
  const invalid: string[] = [];
  for (const c of FOOD_COLUMNS) {
    const cell = row[c];
    // Tolère nombre (JSON) et chaîne (CSV, virgule décimale FR).
    if (typeof cell === 'number') {
      if (Number.isFinite(cell)) values[c] = cell;
      else invalid.push(c);
      continue;
    }
    const raw = String(cell ?? '')
      .trim()
      .replace(',', '.');
    if (raw === '') {
      values[c] = 0;
      continue;
    }
    const n = Number(raw);
    if (Number.isFinite(n)) values[c] = n;
    else invalid.push(c);
  }
  return { values, invalid };
}

/** Index nom (trim+minuscule) → id, sur les aliments visibles (perso + publics). */
async function buildFoodIndex(): Promise<Map<string, string>> {
  const foods = await pb.collection('foods').getFullList<FoodRecord>();
  return new Map(foods.map((f) => [foodKey(f.name), f.id]));
}

/** Clés naturelles (datetime normalisé|ml) des prises d'eau existantes, pour la dédup. */
async function existingWaterKeys(): Promise<Set<string>> {
  const recs = await pb.collection('water_entries').getFullList<WaterEntryRecord>();
  return new Set(recs.map((r) => `${normalizeInstant(r.datetime)}|${r.ml}`));
}

// Liste canonique des activités (source unique : domain/exercise).
const EXERCISE_ACTIVITIES = new Set<string>(SPORT_ORDER);

/** Clés naturelles (datetime|activité|kcal|durée) des séances existantes, pour la dédup. */
async function existingExerciseKeys(): Promise<Set<string>> {
  const recs = await pb.collection('exercise_entries').getFullList<ExerciseEntryRecord>();
  return new Set(
    recs.map((r) => exerciseKey(r.datetime, r.activity, r.kcal_burned, r.duration_min)),
  );
}

// Durée incluse : deux séances à la même minute/activité/kcal mais de durées
// différentes sont légitimement distinctes (sinon la 2e serait perdue au restore).
function exerciseKey(datetime: string, activity: string, kcal: number, duration: number): string {
  return `${normalizeInstant(datetime)}|${activity}|${kcal}|${Number(duration) || 0}`;
}

/**
 * Crée une séance depuis une ligne CSV ou de sauvegarde (dédup partagée via `seen`).
 * Retourne le statut ; l'appelant journalise l'erreur « invalide » (numéro de ligne).
 */
async function createExerciseFromRow(
  row: Record<string, unknown>,
  uid: string | null,
  seen: Set<string>,
  report: ImportReport,
): Promise<'created' | 'skipped' | 'invalid' | 'error'> {
  const datetime = String(row.datetime ?? '').trim();
  const kcal = Number(row.kcal_burned);
  if (!datetime || !Number.isFinite(kcal)) return 'invalid';
  // Activité inconnue → '' (le schéma tolère le vide) plutôt que rejet.
  const rawActivity = String(row.activity ?? '').trim();
  const activity = EXERCISE_ACTIVITIES.has(rawActivity) ? rawActivity : '';
  const parsed = Number(row.duration_min);
  const duration = Number.isFinite(parsed) ? parsed : 0;
  const key = exerciseKey(datetime, activity, kcal, duration);
  if (seen.has(key)) {
    report.skipped++;
    return 'skipped';
  }
  try {
    await pb.collection('exercise_entries').create({
      user: uid,
      datetime,
      activity,
      kcal_burned: kcal,
      duration_min: duration,
      note: String(row.note ?? ''),
    });
    seen.add(key);
    report.created++;
    return 'created';
  } catch (e) {
    report.errors.push(`Séance ${datetime} : ${(e as Error).message}`);
    return 'error';
  }
}

/** Crée un aliment personnel depuis une ligne CSV ou de sauvegarde. Nom trimmé, compo validée. */
function createFood(name: string, row: Record<string, unknown>): Promise<FoodRecord> {
  const { values, invalid } = parseComposition(row);
  if (invalid.length > 0) {
    throw new Error(`valeurs non numériques : ${invalid.join(', ')}`);
  }
  return pb.collection('foods').create<FoodRecord>({
    user: currentUserId(),
    isPublic: false,
    name: name.trim(),
    ...values,
  });
}

// --- Sauvegarde / restauration JSON complète --------------------------------

const SYSTEM_FIELDS = [
  'id',
  'created',
  'updated',
  'collectionId',
  'collectionName',
  'user',
  'expand',
];

// Allow-list des champs de profil restaurables : un JSON manipulé ne peut pas
// injecter de champs arbitraires.
const PROFILE_FIELDS = [
  'birthdate',
  'height_cm',
  'sex',
  'activity_level',
  'ideal_weight_kg',
  'target_weight_kg',
  'calorie_threshold',
  'threshold_mode',
  'protein_target_g',
  'protein_target_mode',
  'protein_per_kg',
  'email_reminders',
  'push_reminders',
  'macro_target_mode',
  'fat_pct',
  'carb_target_g',
  'fat_target_g',
  'water_target_ml',
  'water_target_mode',
  'water_ml_per_kg',
  'weekly_loss_kg',
  'training_phase',
  'surplus_kcal',
] as const;

/** Retire les champs système d'un enregistrement PocketBase. */
function strip(rec: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(rec)) {
    if (!SYSTEM_FIELDS.includes(k)) out[k] = v;
  }
  return out;
}

/** Exporte l'intégralité des données de l'utilisateur en une chaîne JSON. */
export async function exportBackup(): Promise<string> {
  const uid = currentUserId();
  const [profile, weights, milestones, foods, consumptions, water, exercises, favorites] =
    await Promise.all([
      pb
        .collection('profiles')
        .getFirstListItem<ProfileRecord>(`user = ${JSON.stringify(uid)}`)
        .catch(() => null),
      pb.collection('weight_entries').getFullList<WeightEntryRecord>(),
      pb.collection('milestones').getFullList<MilestoneRecord>(),
      pb.collection('foods').getFullList<FoodRecord>({ filter: `user = ${JSON.stringify(uid)}` }),
      pb.collection('consumption_entries').getFullList<ConsumptionRecord>({ expand: 'food' }),
      pb.collection('water_entries').getFullList<WaterEntryRecord>(),
      pb.collection('exercise_entries').getFullList<ExerciseEntryRecord>({ sort: 'datetime' }),
      // Favoris exportés par NOM d'aliment (les id PB ne survivent pas à un erase/réimport).
      pb.collection('food_favorites').getFullList<FoodFavoriteRecord>({ expand: 'food' }),
    ]);

  const data: BackupData = {
    profile: profile ? strip(profile as unknown as Record<string, unknown>) : null,
    weights: weights.map((w) => ({ date: w.date.slice(0, 10), weight_kg: w.weight_kg })),
    milestones: milestones.map((m) => ({
      target_weight_kg: m.target_weight_kg,
      label: m.label,
      achieved_at: m.achieved_at,
    })),
    foods: foods.map((f) => strip(f as unknown as Record<string, unknown>)),
    consumptions: consumptions.map((c) => ({
      datetime: c.datetime,
      meal: c.meal ?? '',
      grams: c.grams,
      food_name: c.expand?.food?.name ?? '',
      ...Object.fromEntries(FOOD_COLUMNS.map((k) => [k, c.expand?.food?.[k] ?? 0])),
    })),
    water: water.map((w) => ({ datetime: w.datetime, ml: w.ml })),
    exercises: exercises.map((e) => ({
      datetime: e.datetime,
      activity: e.activity ?? '',
      kcal_burned: e.kcal_burned,
      duration_min: e.duration_min,
      note: e.note ?? '',
    })),
    // Par nom d'aliment ; les favoris pointant vers un aliment introuvable sont ignorés.
    favorites: favorites
      .map((f) => ({ food_name: f.expand?.food?.name ?? '' }))
      .filter((f) => f.food_name !== ''),
  };
  return serializeBackup(data);
}

/** Restaure une sauvegarde JSON (idempotent, comme l'import CSV). */
export async function importBackup(text: string): Promise<ImportReport> {
  const data = parseBackup(text); // lève une erreur si invalide
  const uid = currentUserId();
  const report: ImportReport = {
    entity: 'Sauvegarde',
    created: 0,
    updated: 0,
    skipped: 0,
    errors: [],
  };

  // Profil (upsert).
  if (data.profile) {
    try {
      const existing = await pb
        .collection('profiles')
        .getFirstListItem<ProfileRecord>(`user = ${JSON.stringify(uid)}`)
        .catch(() => null);
      const payload: Record<string, unknown> = { user: uid };
      for (const field of PROFILE_FIELDS) {
        if (field in data.profile) payload[field] = data.profile[field];
      }
      if (existing) await pb.collection('profiles').update(existing.id, payload);
      else await pb.collection('profiles').create(payload);
      report.updated++;
    } catch (e) {
      report.errors.push(`Profil : ${(e as Error).message}`);
    }
  }

  const foodIndex = await buildFoodIndex();

  // Aliments.
  for (const row of data.foods) {
    const name = String(row.name ?? '').trim();
    if (!name || foodIndex.has(foodKey(name))) {
      if (name) report.skipped++;
      continue;
    }
    try {
      const rec = await createFood(name, row as Record<string, string>);
      foodIndex.set(foodKey(name), rec.id);
      report.created++;
    } catch (e) {
      report.errors.push(`Aliment "${name}" : ${(e as Error).message}`);
    }
  }

  // Pesées (dédup par date).
  const existingW = await pb.collection('weight_entries').getFullList<WeightEntryRecord>();
  const weighedDates = new Set(existingW.map((w) => w.date.slice(0, 10)));
  for (const row of data.weights) {
    const date = String(row.date ?? '').slice(0, 10);
    const kg = Number(row.weight_kg);
    if (!date || !Number.isFinite(kg) || weighedDates.has(date)) {
      report.skipped++;
      continue;
    }
    try {
      await pb.collection('weight_entries').create({ user: uid, date, weight_kg: kg });
      weighedDates.add(date);
      report.created++;
    } catch (e) {
      report.errors.push(`Pesée ${date} : ${(e as Error).message}`);
    }
  }

  // Paliers (dédup par libellé + poids).
  const existingM = await pb.collection('milestones').getFullList<MilestoneRecord>();
  const msKeys = new Set(existingM.map((m) => `${m.label}|${m.target_weight_kg}`));
  for (const row of data.milestones) {
    const label = String(row.label ?? '').trim();
    const target = Number(row.target_weight_kg);
    if (!label || !Number.isFinite(target) || msKeys.has(`${label}|${target}`)) {
      report.skipped++;
      continue;
    }
    try {
      await pb.collection('milestones').create({
        user: uid,
        label,
        target_weight_kg: target,
        achieved_at: row.achieved_at || null,
      });
      msKeys.add(`${label}|${target}`);
      report.created++;
    } catch (e) {
      report.errors.push(`Palier "${label}" : ${(e as Error).message}`);
    }
  }

  // Consommations (auto-création d'aliments + dédup).
  const existingC = await pb.collection('consumption_entries').getFullList<ConsumptionRecord>();
  const seen = new Set(
    existingC.map((r) => `${normalizeInstant(r.datetime)}|${r.food}|${r.grams}`),
  );
  for (const row of data.consumptions) {
    const name = String(row.food_name ?? '').trim();
    const grams = Number(row.grams);
    const datetime = String(row.datetime ?? '').trim();
    if (!name || !datetime || !Number.isFinite(grams)) {
      report.errors.push('Consommation : champ requis invalide');
      continue;
    }
    try {
      let foodId = foodIndex.get(foodKey(name));
      if (!foodId) {
        const rec = await createFood(name, row as Record<string, string>);
        foodId = rec.id;
        foodIndex.set(foodKey(name), foodId);
      }
      const key = `${normalizeInstant(datetime)}|${foodId}|${grams}`;
      if (seen.has(key)) {
        report.skipped++;
        continue;
      }
      await pb.collection('consumption_entries').create({
        user: uid,
        food: foodId,
        grams,
        datetime,
        meal: row.meal || undefined,
      });
      seen.add(key);
      report.created++;
    } catch (e) {
      report.errors.push(`Consommation : ${(e as Error).message}`);
    }
  }

  // Prises d'eau (dédup par datetime|ml).
  const waterSeen = await existingWaterKeys();
  for (const row of data.water) {
    const datetime = String(row.datetime ?? '').trim();
    const ml = Number(row.ml);
    if (!datetime || !Number.isFinite(ml)) {
      report.errors.push('Hydratation : champ requis invalide');
      continue;
    }
    const key = `${normalizeInstant(datetime)}|${ml}`;
    if (waterSeen.has(key)) {
      report.skipped++;
      continue;
    }
    try {
      await pb.collection('water_entries').create({ user: uid, datetime, ml });
      waterSeen.add(key);
      report.created++;
    } catch (e) {
      report.errors.push(`Hydratation : ${(e as Error).message}`);
    }
  }

  // Séances de sport (dédup par datetime|activité|kcal|durée).
  const exerciseSeen = await existingExerciseKeys();
  for (const row of data.exercises) {
    const status = await createExerciseFromRow(row, uid, exerciseSeen, report);
    if (status === 'invalid') report.errors.push('Séance : datetime ou kcal invalide');
  }

  // Favoris : re-liés par NOM d'aliment (retrouvé dans foodIndex, alimenté par la
  // boucle Aliments plus haut), dédup par id local.
  const existingFav = await pb.collection('food_favorites').getFullList<FoodFavoriteRecord>();
  const favFoodIds = new Set(existingFav.map((f) => f.food));
  for (const row of data.favorites) {
    const name = String(row.food_name ?? '').trim();
    if (!name) {
      report.skipped++;
      continue;
    }
    // La ligne favori ne porte que le nom : si l'aliment n'existe pas (perso
    // restauré avant, ou public seedé), on ne fabrique PAS un aliment fantôme
    // à composition nulle — on signale l'orphelin.
    const foodId = foodIndex.get(foodKey(name));
    if (!foodId) {
      report.errors.push(`Favori "${name}" : aliment introuvable`);
      continue;
    }
    if (favFoodIds.has(foodId)) {
      report.skipped++;
      continue;
    }
    try {
      await pb.collection('food_favorites').create({ user: uid, food: foodId });
      favFoodIds.add(foodId);
      report.created++;
    } catch (e) {
      report.errors.push(`Favori "${name}" : ${(e as Error).message}`);
    }
  }

  return report;
}
